<template>
    <div :class="[
        'flag',
        `size-${size}`,
        { 'border-radius': hasBorderRadius },
        { 'border': hasBorder },
        { 'drop-shadow': hasDropShadow },
        gradient,
        className
    ]">
      <img v-if="imageUrl" :src="imageUrl" :alt="code" />
    </div>
  </template>
  
  <script lang="ts" setup>
  import { ref, watchEffect } from 'vue';
  
  // Props
  const props = defineProps({
    size: {
      type: String,
      default: 'm',
      validator: (value: string): boolean => ['s', 'm', 'l'].includes(value.toLowerCase())
    },
    country: {
      type: String,
    },
    hasDropShadow: {
      type: Boolean,
      default: false
    },
    hasBorder: {
      type: Boolean,
      default: false
    },
    hasBorderRadius: {
      type: Boolean,
      default: true
    },
    gradient: {
      type: String,
      validator: (value: string): boolean => ['top-down', 'real-linear', 'real-circular'].includes(value.toLowerCase())
    },
    className: String
  });
  
  // Reactive reference for the image URL
  const imageUrl = ref<string | null>(null);
  
  // Load the image dynamically
  watchEffect(async () => {
    const countryCode = props.country?.toUpperCase();
    const size = props.size.toLowerCase();
    try {
      imageUrl.value = '/flags/svg/'+size+'/'+countryCode+'.svg';
    } catch (error) {
      console.error('Failed to load the flag image:', error);
      imageUrl.value = null; // Handle fallback
    }
  });
  </script>

  <style scoped>
  /* Define reusable styles using utility classes */
.flag-before {
  @apply content-[''] w-full h-full absolute block mix-blend-overlay box-border;
}

.flag {
  @apply inline-block overflow-hidden relative box-border;
}

/* Sizes */
.flag.size-s {
  @apply w-4 h-3; /* 16px x 12px */
}

.flag.size-s.drop-shadow {
  box-shadow: 0 0 1px 0.5px rgba(0, 0, 0, 0.1);
}

.flag.size-s.border-radius {
  border-radius: 1px;
}

.flag.size-m {
  @apply w-5 h-4; /* 20px x 15px */
}

.flag.size-m.drop-shadow {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
}

.flag.size-m.border-radius {
  border-radius: 1.5px;
}

.flag.size-l {
  @apply w-8 h-6; /* 32px x 24px */
}

.flag.size-l.drop-shadow {
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.1);
}

.flag.size-l.border-radius {
  border-radius: 2px;
}

/* Styling with ::before */
.flag.border::before {
  @apply flag-before;
  border: 1px solid rgba(0, 0, 0, 0.5);
}

.flag.border-radius::before {
  @apply flag-before;
  border-radius: 1px;
}

.flag.top-down::before {
  @apply flag-before;
  background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.3) 2%, rgba(255, 255, 255, 0.7) 100%);
}

.flag.real-linear::before {
  @apply flag-before;
  background-image: linear-gradient(
    45deg,
    rgba(0, 0, 0, 0.2) 0%,
    rgba(39, 39, 39, 0.22) 11%,
    rgba(255, 255, 255, 0.3) 27%,
    rgba(0, 0, 0, 0.24) 41%,
    rgba(0, 0, 0, 0.55) 52%,
    rgba(255, 255, 255, 0.26) 63%,
    rgba(0, 0, 0, 0.27) 74%,
    rgba(255, 255, 255, 0.3) 100%
  );
}

.flag.real-circular::before {
  @apply flag-before;
  background: 
    radial-gradient(
      circle at 50% 36%, 
      rgba(255, 255, 255, 0.3) 0%, 
      rgba(0, 0, 0, 0.24) 11%, 
      rgba(0, 0, 0, 0.55) 17%, 
      rgba(255, 255, 255, 0.26) 22%, 
      rgba(0, 0, 0, 0.17) 27%, 
      rgba(255, 255, 255, 0.28) 31%, 
      rgba(255, 255, 255, 0) 37%
    ) center calc(50% - 8px) / 600% 600%, 
    radial-gradient(
      circle at 50% 123%, 
      rgba(255, 255, 255, 0.3) 25%, 
      rgba(0, 0, 0, 0.24) 48%, 
      rgba(0, 0, 0, 0.55) 61%, 
      rgba(255, 255, 255, 0.26) 72%, 
      rgba(0, 0, 0, 0.17) 80%, 
      rgba(255, 255, 255, 0.28) 88%, 
      rgba(255, 255, 255, 0.3) 100%
    ) center calc(50% - 8px) / 600% 600%;
}

/* Image styles */
.flag img {
  @apply block w-full h-full object-cover;
}
  </style>